import { pipe } from "fp-ts/function";
import * as O from "fp-ts/Option";
import * as Rec from "fp-ts/Record";
import { array, empty, object, optional, record, string, union, someRecord, intersection, } from "@discovery/prelude/lib/data/validated";
import { emptyMeta } from "../common";
import { fold } from "../bundle";
import { isOne } from "../association";
const someRelationships = someRecord(optional(record({
    data: union(array(record({ id: string, type: string })), record({ id: string, type: string })),
    meta: optional(object),
})));
const showResourceObject = (ro) => `"${ro.type} ("${ro.id}")`;
const _resourceObject = (type, attributes, relationships, meta) => record({
    attributes,
    id: string,
    // `intersection` is used to save relationships that aren't typed
    relationships: optional(intersection(someRelationships, relationships)),
    meta: optional(meta),
    type,
});
/** resourceObject
 *
 * Creates a validated resource object
 */
const resourceObject = (type, attributes, relationships = someRelationships, meta = emptyMeta) => _resourceObject(type, attributes, relationships, meta);
const bundle2ROI = (b) => fold((s) => [
    isOne(s.single)
        ? record({ id: string, type: string })
        : array(record({ id: string, type: string })),
], (m) => Object.values(m.multiple).map((assoc) => isOne(assoc)
    ? record({ id: string, type: string })
    : array(record({ id: string, type: string }))))(b);
/** relationshipsObject
 *
 * Transforms a relationship mapper to a relationship object that is a validator
 * for the resource object identifiers in a resource object.
 *
 * Example: This will set up resource object identifier on the relationship object
 *
 * ```typescript
 * export const linkRelationships = relationshipsMapper({
 *   images: single2Many(() => Images.Resource.target)),
 *   image: single2One(() => Images.Resource.target)),
 * });
 *
 * const relObjects = relationshipsObject(linkRelationships);
 *
 * export const data = resourceObject(literal("link"), attributes, relObjects);
 * ```
 * Resulting in the following beeing validated
 * ```typescript
 * {
 *   id: ...,
 *   type: ...,
 *   attributes: {...},
 *   meta: {...},
 *   relationships: {
 *     image: {
 *       data: {
 *         id: ...,
 *         type: "image",
 *       }
 *     },
 *     images: {
 *       data: [{
 *         id: ...,
 *         type: "image",
 *       }, {
 *         id: ...,
 *         type: "image",
 *       }]
 *     }
 *   }
 * }
 * ```
 *
 * @param r is a relationship mapper to be transformed
 * @return A RelationshipObject with resource object identifiers
 */
const relationshipsObject = (r, meta = {}) => {
    const rels = pipe(r, Rec.mapWithIndex((k, v) => optional(record({
        data: union(...bundle2ROI(v)),
        meta: pipe(meta, Rec.lookup(k), O.getOrElse(empty), optional),
    }))));
    return record(rels);
};
/**relationshipsMapper
 *
 * Helper for creating a typed relationshipsMapper
 * Normally you can just write:
 *
 * ```typescript
 * const relationships = {
 *   images: single2Many(() => Images.Resource.target)),
 * }
 * ```
 *
 * But that leaves for errors. The following will error if you write it incorrectly
 *
 * ```typescript
 * const relationships = relationshipsMapper({
 *   images: single2Many(() => Images.Resource.target)),
 *   test: "hello" // <----- this would error
 * })
 * ```
 */
const relationshipsMapper = (r) => r;
export * from "./types";
export { relationshipsObject, showResourceObject, someRelationships, resourceObject, relationshipsMapper, };

